import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type {
  ClearQuarantineRequest,
  ClearQuarantineResponse,
  FinancialQuarantine,
  FinancialQuarantineDetail,
  QuarantineResolution,
  ReconciliationOverview,
} from "../../api/staffTypes";
import { Banner, Button, Card, Dialog, EmptyState, PageHeader, QueryView, RadioGroup, StatusPill, Table, Textarea } from "../../components/ui";
import { staffKeys } from "../../layout/StaffShell";
import { AwaitingSecondBanner, DualControlProgress, FilterTabs, Mono, reasonError, shortId, StaffRejection, StepUpNote, useStaffCommand, when } from "./staffCommon";

// The reconciliation override console (UX-DESIGN.md §5; ARCHITECTURE.md
// §4/§13; migration 0072, reconciliation.go). The background worker
// re-derives every materialized money figure from the ledger in one
// snapshot; a mismatch quarantines only the party that owns the figure
// (an advertiser takes no new reservations, a publisher gets no payouts),
// and an unbalanced journal entry halts every payout. This screen shows
// what is quarantined and why, the cumulative discrepancy, the funding
// picture the override would draw on, and the dual-controlled clear:
// two distinct reviewers, step-up, the same resolution, the same
// quarantine version. The degraded-mode override itself is not built
// (the server answers 501 override_not_implemented), and the page says so.

const CHECK_LABEL: Record<string, string> = {
  account_balance: "Account balance vs its postings",
  account_posting_sequence: "Account posting count",
  journal_entry_balance: "Journal entry does not balance",
  budget_period_reserved: "Budget period reserved vs reservations",
  budget_period_captured: "Budget period captured vs reservations",
  deposit_refunded_total: "Deposit refunded total vs its events",
  deposit_disputed_total: "Deposit disputed total vs its events",
  posting_claimed_total: "Posting claimed total vs payout claims",
  // Migration 0075
  promo_grant_remaining: "Promo grant remaining vs its credit movements",
  promo_credit_account: "Promo credit account vs its credit movements",
  reservation_promo_split: "Reservation promo portion vs the grants it drew",
  promo_commitments_balance: "Promo commitments vs outstanding promo obligations",
};

const BLOCK_LABEL: Record<string, string> = { new_reservations: "New reservations", payouts: "Payouts" };

function checkLabel(c: string): string {
  return CHECK_LABEL[c] ?? c.replace(/_/g, " ");
}

function levelPill(level: number) {
  if (level >= 3) return <StatusPill tone="error">Critical (level 3)</StatusPill>;
  if (level === 2) return <StatusPill tone="warning">Escalated (level 2)</StatusPill>;
  return <StatusPill tone="info">Level {level}</StatusPill>;
}

function age(seconds: number): string {
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} days`;
}

function partyOf(q: FinancialQuarantine): string {
  if (q.scope === "platform_payouts") return "Whole platform (ledger)";
  return `${q.party_name ?? q.party_type} (${shortId(q.party_id)})`;
}

const recKeys = {
  overview: ["staff", "reconciliation", "overview"] as const,
  list: (status: string) => ["staff", "reconciliation", "quarantines", status] as const,
  detail: (id: string) => ["staff", "reconciliation", "quarantine", id] as const,
};

export function ReconciliationPage() {
  const ep = useEndpoints();
  const [status, setStatus] = useState<"open" | "cleared" | "all">("open");
  const overview = useQuery({ queryKey: recKeys.overview, queryFn: () => ep.staff.reconciliation.overview() });
  const list = useQuery({ queryKey: recKeys.list(status), queryFn: () => ep.staff.reconciliation.quarantines(status) });
  return (
    <div className="stack-lg">
      <PageHeader
        title="Reconciliation"
        description="Materialized balances checked against the append-only ledger. A mismatch halts new reservations or payouts for the implicated account only."
      />
      <QueryView query={overview} loadingLabel="Reading the last reconciliation run">
        {(o) => <OverviewCards o={o} />}
      </QueryView>
      <Card title="Quarantines" actions={
        <FilterTabs
          label="Quarantine status"
          value={status}
          onChange={setStatus}
          options={[
            { value: "open", label: "Open" },
            { value: "cleared", label: "Cleared" },
            { value: "all", label: "All" },
          ]}
        />
      }>
        <QueryView query={list} loadingLabel="Loading quarantines">
          {(d) => (
            <Table
              caption="Financial quarantines"
              captionHidden
              rows={d.quarantines}
              rowKey={(q) => q.quarantine_id}
              empty={<EmptyState title={status === "open" ? "Nothing is quarantined" : "No quarantines"}>No quarantines match this filter.</EmptyState>}
              columns={[
                { key: "id", header: "Quarantine", cell: (q) => <Link to={`/staff/reconciliation/${q.quarantine_id}`}>{shortId(q.quarantine_id)}</Link> },
                { key: "party", header: "Account", cell: (q) => partyOf(q) },
                { key: "reason", header: "Opened by", cell: (q) => checkLabel(q.reason) },
                { key: "blocks", header: "Halts", cell: (q) => q.blocks.map((b) => BLOCK_LABEL[b] ?? b).join(", ") || "Nothing customer-facing" },
                { key: "disc", header: "Current discrepancy", numeric: true, cell: (q) => <Mono>{q.current_discrepancy}</Mono> },
                { key: "level", header: "Escalation", cell: (q) => (q.status === "open" ? levelPill(q.escalation_level) : <StatusPill tone="success">Cleared</StatusPill>) },
                { key: "age", header: "Age", cell: (q) => age(q.age_seconds) },
              ]}
            />
          )}
        </QueryView>
      </Card>
    </div>
  );
}

function OverviewCards({ o }: { o: ReconciliationOverview }) {
  return (
    <>
      {o.platform_payouts_halted ? (
        <Banner tone="error" live="polite" title="Every payout is halted: the ledger does not balance">
          A journal entry's postings do not sum to zero. That breaks the ledger's own invariant, so payouts to every publisher are
          refused until two reviewers clear the platform quarantine. Serving continues.
        </Banner>
      ) : null}
      <Card title="Last run">
        {o.last_run ? (
          <dl className="definition-list">
            <dt>Finished</dt>
            <dd>
              {when(o.last_run.finished_at)} <span className="xs muted">run {o.last_run.run_id} · {o.last_run.lease_owner}</span>
            </dd>
            <dt>Checked</dt>
            <dd>
              {o.last_run.accounts_checked} accounts, {o.last_run.budget_periods_checked} budget periods, {o.last_run.deposits_checked} deposits,{" "}
              {o.last_run.claim_totals_checked} claimed postings
            </dd>
            <dt>Findings</dt>
            <dd>
              {o.last_run.findings} · ledger {o.last_run.ledger_balanced ? "balances" : <strong>does not balance</strong>} · tolerance{" "}
              <Mono>{o.last_run.tolerance}</Mono>
            </dd>
            <dt>Open quarantines</dt>
            <dd>{o.open_quarantines}</dd>
            <dt>Aggregate discrepancy</dt>
            <dd>
              <Mono>{o.aggregate_discrepancy}</Mono> <span className="xs muted">(sum over open account quarantines)</span>
            </dd>
          </dl>
        ) : (
          <p className="small">No reconciliation run has finished yet. Nothing here means “verified”.</p>
        )}
      </Card>
      <Card title="Degraded-mode override">
        <div className="stack-sm">
          <Banner tone="neutral" title="Not built">
            {o.override.detail}
          </Banner>
          <dl className="definition-list">
            <dt>Hard cap (configuration)</dt>
            <dd>
              <Mono>{o.override.cap}</Mono>
            </dd>
            {o.risk_funding.length ? (
              o.risk_funding.map((f) => (
                <Fragment key={f.currency}>
                  <dt>{f.currency}: platform_risk_spend_asset</dt>
                  <dd>
                    <Mono>{f.platform_risk_spend_asset}</Mono>
                  </dd>
                  <dt>{f.currency}: drawn (degraded_serving_subsidy_expense)</dt>
                  <dd>
                    <Mono>{f.degraded_serving_subsidy_expense}</Mono>
                  </dd>
                </Fragment>
              ))
            ) : (
              <>
                <dt>Risk funding</dt>
                <dd>No platform_risk_spend_asset account exists yet; nothing has been set aside or drawn.</dd>
              </>
            )}
          </dl>
        </div>
      </Card>
    </>
  );
}

export function QuarantineDetailPage() {
  const { quarantineId = "" } = useParams();
  const ep = useEndpoints();
  const q = useQuery({ queryKey: recKeys.detail(quarantineId), queryFn: () => ep.staff.reconciliation.quarantine(quarantineId) });
  return (
    <div className="stack-lg">
      <PageHeader title={`Quarantine ${shortId(quarantineId)}`} back={{ to: "/staff/reconciliation", label: "Reconciliation" }} />
      <QueryView query={q} loadingLabel="Loading the quarantine">
        {(d) => <QuarantineDetail d={d} />}
      </QueryView>
    </div>
  );
}

function QuarantineDetail({ d }: { d: FinancialQuarantineDetail }) {
  const current = d.votes.filter((v) => v.current);
  const stale = d.votes.length - current.length;
  return (
    <div className="stack">
      <Card title="Quarantine" actions={d.status === "open" ? levelPill(d.escalation_level) : <StatusPill tone="success">Cleared</StatusPill>}>
        <dl className="definition-list">
          <dt>Account</dt>
          <dd>
            {partyOf(d)} <span className="xs muted mono">{d.party_id}</span>
          </dd>
          <dt>Halts</dt>
          <dd>{d.blocks.map((b) => BLOCK_LABEL[b] ?? b).join(", ") || "Nothing customer-facing (a platform account)"}</dd>
          <dt>Opened by</dt>
          <dd>{checkLabel(d.reason)}</dd>
          <dt>Opened / last checked</dt>
          <dd>
            {when(d.opened_at)} ({age(d.age_seconds)}) / {when(d.last_checked_at)}
          </dd>
          <dt>Discrepancy: first seen / current / peak / cumulative</dt>
          <dd>
            <Mono>{d.first_seen_discrepancy}</Mono> / <Mono>{d.current_discrepancy}</Mono> / <Mono>{d.peak_discrepancy}</Mono> /{" "}
            <Mono>{d.cumulative_discrepancy}</Mono>
          </dd>
          <dt>Version</dt>
          <dd>
            {d.version} <span className="xs muted">changed {when(d.last_changed_at)}; votes bind to it</span>
          </dd>
          {d.status === "cleared" ? (
            <>
              <dt>Cleared</dt>
              <dd>
                {when(d.cleared_at)} · {d.resolution} · {d.resolution_explanation}
              </dd>
            </>
          ) : null}
        </dl>
      </Card>
      <Card title="Discrepancies">
        <Table
          caption="Discrepancies"
          captionHidden
          rows={d.discrepancies}
          rowKey={(x) => x.discrepancy_id}
          empty={<EmptyState title="No discrepancies">None recorded.</EmptyState>}
          columns={[
            { key: "check", header: "Check", cell: (x) => checkLabel(x.check) },
            { key: "subject", header: "Subject", cell: (x) => <Mono>{`${x.subject_type} ${x.subject_key}`}</Mono> },
            { key: "mat", header: "Materialized", numeric: true, cell: (x) => <Mono>{x.materialized}</Mono> },
            { key: "exp", header: "Ledger says", numeric: true, cell: (x) => <Mono>{x.expected}</Mono> },
            {
              key: "diff",
              header: "Difference",
              numeric: true,
              cell: (x) => (
                <Mono>
                  {x.difference}
                  {x.baseline !== "0" && !/^0\.0+$/.test(x.baseline) ? ` (accepted ${x.baseline})` : ""}
                  {x.unit === "count" ? " postings" : x.currency ? ` ${x.currency}` : ""}
                </Mono>
              ),
            },
            {
              key: "state",
              header: "State",
              cell: (x) => (
                <StatusPill tone={x.state === "present" ? "error" : x.state === "consistent" ? "success" : "neutral"}>
                  {x.state === "present" ? "Still present" : x.state === "consistent" ? "Consistent again" : "Closed"}
                </StatusPill>
              ),
            },
            { key: "seen", header: "Seen", cell: (x) => `${when(x.first_seen_at)} · ${x.observations}×` },
          ]}
        />
      </Card>
      {d.status === "open" ? (
        <Card title="Clear this quarantine">
          <div className="stack">
            <DualControlProgress
              approvals={current.length}
              required={d.approvals_required}
              votes={current.map((v) => ({ actor_user_id: v.actor_user_id, decision: v.resolution, decided_at: v.decided_at }))}
              label="Clear votes at this version"
            />
            {stale > 0 ? (
              <p className="xs muted">
                {stale} earlier vote(s) were cast against an older version of this quarantine and no longer count.
              </p>
            ) : null}
            <ClearForm d={d} />
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function ClearForm({ d }: { d: FinancialQuarantineDetail }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [resolution, setResolution] = useState<QuarantineResolution | "">("");
  const [explanation, setExplanation] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const cmd = useStaffCommand<ClearQuarantineRequest, ClearQuarantineResponse>(
    (req, opts) => ep.staff.reconciliation.clear(d.quarantine_id, req, opts),
    () => {
      setConfirming(false);
      setExplanation("");
      void qc.invalidateQueries({ queryKey: ["staff", "reconciliation"] });
      void qc.invalidateQueries({ queryKey: staffKeys.quarantines });
    },
  );
  const present = d.open_discrepancies;
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!resolution) return setFormError("Choose how it was resolved.");
    const re = reasonError(explanation);
    if (re) return setFormError(re);
    if (resolution === "repaired" && present > 0) {
      return setFormError(`${present} discrepancy(ies) are still present at the last run. Repair them first, or explain them.`);
    }
    setFormError(null);
    cmd.reset();
    setConfirming(true);
  }
  const res = cmd.data;
  return (
    <div className="stack">
      {res?.result === "awaiting_second_approval" ? <AwaitingSecondBanner approvals={res.approvals} required={res.approvals_required} /> : null}
      {res?.result === "cleared" ? (
        <Banner tone="success" live="polite" title="Cleared">
          The quarantine is cleared ({res.resolution}). The account's reservations or payouts resume.
        </Banner>
      ) : null}
      <form className="stack" onSubmit={submit} noValidate>
        <RadioGroup<QuarantineResolution>
          legend="Resolution"
          name="quarantine-resolution"
          value={resolution}
          onChange={setResolution}
          options={[
            {
              value: "repaired",
              label: "Repaired",
              hint: "The figures agree with the ledger again (the last run found every discrepancy consistent).",
            },
            {
              value: "explained",
              label: "Explained",
              hint: "The difference is understood and accepted as-is: it becomes a baseline, and any further movement opens a new quarantine.",
            },
          ]}
        />
        <Textarea label="Explanation" value={explanation} onChange={(e) => setExplanation(e.target.value)} rows={3} />
        <StepUpNote required />
        <p className="xs muted">
          Two distinct reviewers must record the same resolution against version {d.version}. If a run changes the picture first,
          the server refuses the stale vote.
        </p>
        {formError ? (
          <Banner tone="error" live="alert">
            {formError}
          </Banner>
        ) : null}
        {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="secondary">
            Record clear vote…
          </Button>
        </div>
      </form>
      <Dialog
        open={confirming}
        alert
        onClose={() => setConfirming(false)}
        dismissible={!cmd.isPending}
        title="Record your clear vote?"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={cmd.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={cmd.isPending}
              onClick={() => cmd.run({ resolution: resolution as QuarantineResolution, explanation: explanation.trim(), expected_version: d.version })}
            >
              Record vote
            </Button>
          </>
        }
      >
        <div className="stack-sm small">
          <span>
            Resolution: <strong>{resolution}</strong>, against version {d.version}. Nothing is lifted until a second, different
            reviewer records the same resolution; then {d.blocks.map((b) => (BLOCK_LABEL[b] ?? b).toLowerCase()).join(" and ") || "the quarantine"}{" "}
            resume for this account.
          </span>
          {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        </div>
      </Dialog>
    </div>
  );
}
